import { authService } from '@/api/services/authService';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useHealth } from '@/contexts/HealthContext';
import { useLanguage } from '@/i18n/language-hooks';
import { useRoutePrefetch } from '@/lib/queryPrefetch';
import { cn } from '@/lib/utils';
import {
  CandlestickChart,
  LayoutGrid,
  type LucideIcon,
  Mail,
  PanelLeftClose,
  PanelRightClose,
  Settings,
  TrendingUp,
  Wallet,
} from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { AppWordmark } from './AppWordmark';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  onContactFormOpen: () => void;
}

interface NavItem {
  icon: LucideIcon;
  label: string;
  href: string;
}

function isPathActive(pathname: string, href: string): boolean {
  if (href === '/overview') {
    return pathname === '/' || pathname === '/overview';
  }
  if (href === '/banking/accounts') {
    return (
      pathname === '/banking' ||
      pathname === '/banking/accounts' ||
      pathname === '/accounts' ||
      pathname.startsWith('/accounts/') ||
      pathname.startsWith('/banking/')
    );
  }
  if (href === '/planning') {
    return pathname === '/planning' || pathname.startsWith('/planning/');
  }
  if (href === '/trade') {
    return pathname === '/trade' || pathname.startsWith('/trade/');
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

const collapsedTooltipClass =
  'border-fin-gray-700 bg-fin-gray-900 text-fin-white text-xs font-medium';

const navRowClass = (collapsed: boolean) =>
  cn(
    'flex w-full items-center rounded-lg px-3 py-2.5 text-sm transition-[gap] duration-200 ease-linear',
    collapsed ? 'gap-0' : 'gap-3'
  );

const navLabelClass = (collapsed: boolean) =>
  cn(
    'truncate transition-[max-width,opacity] duration-200 ease-linear',
    collapsed ? 'max-w-0 overflow-hidden opacity-0' : 'max-w-[12rem] opacity-100'
  );

/** Sidebar nav row with a portaled tooltip when the rail is collapsed. */
function NavLink({
  item,
  active,
  collapsed,
  onPrefetch,
}: {
  item: NavItem;
  active: boolean;
  collapsed: boolean;
  onPrefetch: (href: string) => void;
}) {
  const Icon = item.icon;
  const link = (
    <Link
      to={item.href}
      onMouseEnter={() => onPrefetch(item.href)}
      onTouchStart={() => onPrefetch(item.href)}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'group/nav relative',
        navRowClass(collapsed),
        active
          ? 'bg-fin-gray-800 text-fin-white'
          : 'text-fin-gray-400 hover:bg-fin-gray-800/60 hover:text-fin-gray-300'
      )}
    >
      {/* Animated active rail indicator */}
      <span
        className={cn(
          'bg-fin-accent absolute top-1/2 left-0 w-0.5 -translate-y-1/2 rounded-full transition-[height,opacity] duration-200 ease-out motion-reduce:transition-none',
          active ? 'h-5 opacity-100' : 'h-0 opacity-0'
        )}
        aria-hidden
      />
      <Icon
        className={cn(
          'size-[18px] shrink-0 transition-[transform,color] duration-200 ease-out motion-reduce:transform-none motion-reduce:transition-none',
          active
            ? 'text-fin-accent scale-110'
            : 'opacity-90 group-hover/nav:scale-105 group-hover/nav:opacity-100'
        )}
        aria-hidden
      />
      <span className={navLabelClass(collapsed)} aria-hidden={collapsed}>
        {item.label}
      </span>
      {collapsed && <span className='sr-only'>{item.label}</span>}
    </Link>
  );

  if (!collapsed) {
    return link;
  }

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>{link}</TooltipTrigger>
      <TooltipContent side='right' sideOffset={8} className={collapsedTooltipClass}>
        {item.label}
      </TooltipContent>
    </Tooltip>
  );
}

/** Generic rail action button (contact) with portaled tooltip when collapsed. */
function RailButton({
  icon: Icon,
  label,
  collapsed,
  onClick,
}: {
  icon: LucideIcon;
  label: string;
  collapsed: boolean;
  onClick: () => void;
}) {
  const button = (
    <button
      type='button'
      onClick={onClick}
      className={cn(
        navRowClass(collapsed),
        'text-fin-gray-400 hover:bg-fin-gray-800/60 hover:text-fin-gray-300'
      )}
    >
      <Icon className='size-[18px] shrink-0 opacity-90' aria-hidden />
      <span className={navLabelClass(collapsed)} aria-hidden={collapsed}>
        {label}
      </span>
      {collapsed && <span className='sr-only'>{label}</span>}
    </button>
  );

  if (!collapsed) {
    return button;
  }

  return (
    <Tooltip delayDuration={0}>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent side='right' sideOffset={8} className={collapsedTooltipClass}>
        {label}
      </TooltipContent>
    </Tooltip>
  );
}

function StatusDot({ className, dotClassName }: { className?: string; dotClassName?: string }) {
  const { isHealthy, isLoading, error } = useHealth();
  const { t } = useLanguage();

  const tone = isLoading
    ? { color: 'bg-fin-warning', label: t('connectivity.checking') }
    : isHealthy
      ? { color: 'bg-fin-green', label: t('connectivity.online') }
      : error
        ? { color: 'bg-fin-danger', label: t('connectivity.offline') }
        : { color: 'bg-fin-warning', label: t('connectivity.degraded') };

  return (
    <span className={cn('inline-flex items-center gap-1.5', className)} title={tone.label}>
      <span className={cn('size-1.5 rounded-full', tone.color, dotClassName)} aria-hidden />
      <span className='sr-only'>{tone.label}</span>
    </span>
  );
}

export const Sidebar = ({ collapsed, onToggleCollapse, onContactFormOpen }: SidebarProps) => {
  const { t } = useLanguage();
  const location = useLocation();
  const isAuthenticated = authService.isAuthenticated();
  const prefetch = useRoutePrefetch();

  const pillars: NavItem[] = [
    { icon: LayoutGrid, label: t('nav.overview'), href: '/overview' },
    { icon: Wallet, label: t('nav.banking'), href: '/banking/accounts' },
    { icon: CandlestickChart, label: t('nav.trade'), href: '/trade' },
    { icon: TrendingUp, label: t('nav.planning'), href: '/planning' },
    { icon: Settings, label: t('settings.title'), href: '/settings' },
  ];

  return (
    <nav
      className={cn(
        'bg-fin-sidebar relative z-20 hidden min-h-0 shrink-0 flex-col transition-[width] duration-200 ease-linear md:flex',
        collapsed ? 'w-[72px]' : 'w-[260px]'
      )}
      aria-label={t('app.name')}
    >
      {/* App name + version / status block */}
      <div className='flex shrink-0 flex-col px-3 py-3'>
        {collapsed ? (
          <button
            type='button'
            onClick={onToggleCollapse}
            className='group/expand text-fin-gray-400 hover:bg-fin-gray-800/80 hover:text-fin-white relative flex size-8 items-center justify-center rounded-lg transition-colors'
            aria-label={t('layout.sidebar.expand')}
            aria-expanded={false}
          >
            <span className='absolute inset-0 flex items-center justify-center transition-opacity duration-150 group-hover/expand:opacity-0'>
              <span className='relative'>
                <AppWordmark name={t('app.name')} showName={false} />
                <StatusDot
                  className='absolute -right-0.5 -bottom-0.5 gap-0'
                  dotClassName='ring-fin-sidebar size-2 ring-2'
                />
              </span>
            </span>
            <PanelRightClose
              className='size-5 opacity-0 transition-opacity duration-150 group-hover/expand:opacity-100 group-focus-visible/expand:opacity-100'
              aria-hidden
            />
          </button>
        ) : (
          <div className='flex items-start justify-between gap-2'>
            <AppWordmark
              name={t('app.name')}
              className='min-w-0 flex-1'
              meta={
                <div className='text-fin-gray-500 flex items-center gap-2 text-xs'>
                  <StatusDot />
                  <span>v{t('app.version')}</span>
                </div>
              }
            />
            <button
              type='button'
              onClick={onToggleCollapse}
              className='text-fin-gray-400 hover:bg-fin-gray-800 hover:text-fin-white shrink-0 rounded-md p-2 transition-colors'
              aria-label={t('layout.sidebar.collapse')}
              aria-expanded
            >
              <PanelLeftClose className='size-5' aria-hidden />
            </button>
          </div>
        )}
      </div>

      <div className='flex min-h-0 flex-1 flex-col px-3 pt-3 pb-4'>
        <ul className='flex shrink-0 flex-col gap-1'>
          {pillars.map((item) => (
            <li key={item.href}>
              <NavLink
                item={item}
                active={isPathActive(location.pathname, item.href)}
                collapsed={collapsed}
                onPrefetch={prefetch}
              />
            </li>
          ))}
        </ul>

        <div className='flex-1' />

        {/* Pinned last items */}
        <ul className='flex shrink-0 flex-col gap-1 pt-3'>
          {isAuthenticated && (
            <li className='w-full'>
              <RailButton
                icon={Mail}
                label={t('footer.help.contactUs')}
                collapsed={collapsed}
                onClick={onContactFormOpen}
              />
            </li>
          )}
        </ul>
      </div>
    </nav>
  );
};
